import { BarChart3, ShieldCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import { useBranding } from '~/helpers/branding/branding-context'
import { useAuth } from '~/helpers/store/auth'
import { useStatsNoticeDialog } from '~/helpers/store/consent'

/**
 * Notice d'information RGPD « suivi statistique » (art. 13).
 *
 * Ce n'est pas un recueil de consentement : le suivi d'assiduité repose sur
 * l'exécution du contrat de formation (art. 6.1.b) et sur l'obligation légale de
 * traçabilité de l'organisme (art. 6.1.c). L'apprenant est donc informé, pas
 * sollicité — il n'y a rien à accorder ni à refuser (#133).
 *
 * Elle ne s'ouvre plus d'elle-même et ne demande plus d'acquittement : les CGU,
 * acceptées par tout utilisateur à la connexion (#360), annoncent déjà le suivi.
 * Elle reste consultable à tout moment depuis « Mes données ».
 */
function StatsNoticeDialog() {
  const { payload } = useAuth()
  const { appName, loaded: brandingLoaded } = useBranding()
  const { t } = useTranslation()
  const { manualOpen, close } = useStatsNoticeDialog()

  if (!payload) return null

  // La notice cite le nom de la plateforme dès sa première phrase : on attend le
  // branding, sinon le nom par défaut se corrige sous les yeux du lecteur (#35).
  const open = brandingLoaded && manualOpen

  return (
    <Dialog open={open} onOpenChange={next => !next && close()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <div className="flex items-center gap-2 text-primary-text">
            <ShieldCheck className="size-5" />
            <DialogTitle>{t('notice.stats.title')}</DialogTitle>
          </div>
          <DialogDescription className="pt-2">
            {t('notice.stats.intro', { appName })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm text-muted-foreground">
          <div className="flex items-start gap-2">
            <BarChart3 className="size-4 mt-0.5 shrink-0 text-primary-text" />
            <p>{t('notice.stats.purpose')}</p>
          </div>

          <ul className="list-disc pl-5 space-y-1">
            <li>{t('notice.stats.data')}</li>
            <li>{t('notice.stats.legalBasis')}</li>
            <li>{t('notice.stats.rights')}</li>
          </ul>

          <p className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-foreground">
            {t('notice.stats.noConsentNeeded')}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default StatsNoticeDialog
